import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ORGANIZATION_CATEGORY_SHORT_LABELS,
  VERIFICATION_DOCUMENT_TYPE_LABELS,
  type OrganizationCategory,
} from '@safetyhub/shared';
import { Card } from '@safetyhub/ui';
import {
  getPendingVerifications,
  type PendingOrg,
  type VerificationStatusFilter,
} from '../../lib/verification';
import VerificationDetailModal from '../../components/VerificationDetailModal';
import { BusinessStatusBadge } from '../../components/BusinessStatusBadge';

function AutoCheckBadge({ label, passed }: { label: string; passed: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        passed
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      {label}
    </span>
  );
}

function DocumentIndicator({ uploaded, notApplicable }: { uploaded: boolean; notApplicable?: boolean }) {
  if (notApplicable) {
    return <span className="text-gray-300">—</span>;
  }
  if (uploaded) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }
  return <span className="text-gray-300">—</span>;
}

function ApprovalMethodBadge({ verifiedBySuperAdminId }: { verifiedBySuperAdminId: string | null }) {
  if (verifiedBySuperAdminId) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-800">
        수동 승인
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
      자동 승인
    </span>
  );
}

const TABS: { value: VerificationStatusFilter; label: string }[] = [
  { value: 'pending', label: '심사 대기' },
  { value: 'rejected', label: '반려됨' },
  { value: 'verified', label: '인증 완료' },
  { value: 'all', label: '전체' },
];

const TOTAL_LABELS: Record<VerificationStatusFilter, string> = {
  pending: '대기 중',
  rejected: '반려됨',
  verified: '인증 완료',
  all: '',
};

export default function VerificationQueue() {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<string>('');
  const [manualReviewOnly, setManualReviewOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<VerificationStatusFilter>('pending');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'verification', 'pending', page, category, manualReviewOnly, statusFilter],
    queryFn: () =>
      getPendingVerifications({
        page,
        limit,
        category: category || undefined,
        manualReviewOnly: statusFilter === 'pending' ? (manualReviewOnly || undefined) : undefined,
        statusFilter,
      }),
  });

  const organizations: PendingOrg[] = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const totalLabel = TOTAL_LABELS[statusFilter];
  const isVerifiedTab = statusFilter === 'verified';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">인증 심사 큐</h2>
        <div className="flex items-center gap-4 flex-wrap">
          {!isLoading && (
            <span className="text-sm text-gray-500">
              총 {total}건{totalLabel ? ` ${totalLabel}` : ''}
            </span>
          )}
          {statusFilter === 'pending' && (
            <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={manualReviewOnly}
                onChange={(e) => {
                  setManualReviewOnly(e.target.checked);
                  setPage(1);
                }}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              수동 심사 필요만 보기
            </label>
          )}
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">전체 카테고리</option>
            <option value="WORKPLACE">사업장</option>
            <option value="GUIDANCE_AGENCY">지도기관</option>
            <option value="SAFETY_MANAGEMENT_AGENCY">안전관리대행기관</option>
          </select>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => {
                setStatusFilter(tab.value);
                setPage(1);
              }}
              className={`py-3 px-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                statusFilter === tab.value
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      )}

      {!isLoading && error && (
        <div className="bg-red-50 p-4 rounded text-red-600">
          데이터를 불러오는데 실패했습니다.
        </div>
      )}

      {!isLoading && !error && (
        <>
          <Card padding="none" radius="lg" className="overflow-x-auto">
            <table className="min-w-max w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    조직명 / 사업자번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    카테고리
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {isVerifiedTab ? '승인일' : '신청일'}
                  </th>
                  {!isVerifiedTab && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      자동 검증
                    </th>
                  )}
                  {!isVerifiedTab && (
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {VERIFICATION_DOCUMENT_TYPE_LABELS.BUSINESS_REGISTRATION}
                    </th>
                  )}
                  {!isVerifiedTab && (
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {VERIFICATION_DOCUMENT_TYPE_LABELS.DESIGNATION_CERT}
                    </th>
                  )}
                  {isVerifiedTab && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      승인 방식
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {organizations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isVerifiedTab ? 4 : 6}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      {statusFilter === 'pending' && '대기 중인 인증 신청이 없습니다.'}
                      {statusFilter === 'rejected' && '반려된 조직이 없습니다.'}
                      {statusFilter === 'verified' && '인증 완료된 조직이 없습니다.'}
                      {statusFilter === 'all' && '조회된 조직이 없습니다.'}
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => {
                    const { autoCheckSummary } = org;
                    const businessStatus = org.ntsValidation?.businessStatus;
                    return (
                      <tr
                        key={org.id}
                        onClick={() => setSelectedOrgId(org.id)}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">{org.name}</span>
                            {org.isManualReview && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                심사 필요
                              </span>
                            )}
                            {org.verificationStatus === 'REVIEW_AMBIGUOUS' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                후보 다수
                              </span>
                            )}
                            {businessStatus && businessStatus !== 'ACTIVE' && (
                              <BusinessStatusBadge value={businessStatus} />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {org.registrationNumber ?? '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {ORGANIZATION_CATEGORY_SHORT_LABELS[org.category as OrganizationCategory] ?? org.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isVerifiedTab
                            ? (org.verifiedAt ? new Date(org.verifiedAt).toLocaleDateString('ko-KR') : '-')
                            : new Date(org.createdAt).toLocaleDateString('ko-KR')}
                        </td>
                        {!isVerifiedTab && (
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex gap-1.5 items-center">
                              <AutoCheckBadge label="체크섬" passed={autoCheckSummary.checksumPassed} />
                              <AutoCheckBadge label="NTS" passed={autoCheckSummary.ntsPassed} />
                              <AutoCheckBadge label="공시매칭" passed={autoCheckSummary.hasMatch} />
                            </div>
                          </td>
                        )}
                        {!isVerifiedTab && (
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <DocumentIndicator uploaded={org.hasBusinessReg} />
                          </td>
                        )}
                        {!isVerifiedTab && (
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <DocumentIndicator uploaded={org.hasDesignation} notApplicable={org.category === 'WORKPLACE'} />
                          </td>
                        )}
                        {isVerifiedTab && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ApprovalMethodBadge verifiedBySuperAdminId={org.verifiedBySuperAdminId} />
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                다음
              </button>
            </div>
          )}
        </>
      )}

      <VerificationDetailModal
        companyId={selectedOrgId}
        isOpen={!!selectedOrgId}
        onClose={() => setSelectedOrgId(null)}
      />
    </div>
  );
}
